import { MAP_LAYER_PANEL_WIDTH_CLASS } from '@/features/map/components/mapBarGeometry';

export * from './dealStages';
export * from './mapBubbleSurface';

/** Width of the deal, place and location cards: the same as the layer panels and the workspace menu. */
export const MAP_DETAIL_CARD_WIDTH = MAP_LAYER_PANEL_WIDTH_CLASS;

/** Viewport under the 3.5rem navbar. The shell and chrome grow with a tall card. */
export const MAP_VIEWPORT_MIN_HEIGHT_CLASS = 'min-h-[calc(100dvh-3.5rem)]';

/** Card + side panel no longer fit; extra details open in a popup. */
export const MAP_DETAIL_POPUP_BELOW_PX = 584;

export const METRICS_LIST = {
  MAX_AADT: 'max_aadt',
  CLOSEST_AADT: 'closest_aadt',
  SUM_AADT: 'sum_aadt',
  POPULATION_DENSITY: 'population_density',
  LOCAL_POPULATION_DENSITY: 'local_population_density',
  NUMBER_HPC_STATIONS: 'number_hpc_stations',
  AVERAGE_CHARGES_STATION_PER_DAY: 'average_charges_per_station_per_day',
  HIGHEST_PERFORMING_COMPETITOR: 'highest_performing_competitor',
  CAPTURED_AADT: 'captured_aadt',
  FORECAST: 'forecast',
} as const;

export type MetricsList = (typeof METRICS_LIST)[keyof typeof METRICS_LIST];

/** Human-readable label for a metric value (keyed by MetricsList constant). */
export const METRIC_LABELS: Record<MetricsList, string> = {
  [METRICS_LIST.MAX_AADT]: 'Max AADT',
  [METRICS_LIST.CLOSEST_AADT]: 'Closest AADT',
  [METRICS_LIST.SUM_AADT]: 'Sum AADT',
  [METRICS_LIST.POPULATION_DENSITY]: 'Population density',
  [METRICS_LIST.LOCAL_POPULATION_DENSITY]: 'Local population density',
  [METRICS_LIST.NUMBER_HPC_STATIONS]: 'HPC stations nearby',
  [METRICS_LIST.AVERAGE_CHARGES_STATION_PER_DAY]: 'Avg charges / station / day',
  [METRICS_LIST.HIGHEST_PERFORMING_COMPETITOR]: 'Highest performing competitor',
  [METRICS_LIST.CAPTURED_AADT]: 'Captured AADT',
  [METRICS_LIST.FORECAST]: 'Forecast',
};

/**
 * Size for a metric-selection checkbox row (shared by BatchUploadCSVModal and
 * ComputePreferencesModal so both metric pickers read as one control) — the
 * base `Checkbox` component's default size and checked-state color
 * (`primary-700`), matching ComputePreferencesModal's original, unstyled
 * checkboxes.
 */
export const METRIC_CHECKBOX_CLASSNAME = 'size-5';

/**
 * Label style for a metric-selection checkbox row (shared for the same
 * reason as `METRIC_CHECKBOX_CLASSNAME`) — plain text color, matching
 * ComputePreferencesModal's original label styling.
 */
export const METRIC_LABEL_CLASSNAME =
  'text-xs font-normal cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-50';

/** Metrics always requested when creating a location-based project. */
export const DEFAULT_LOCATION_METRICS: readonly MetricsList[] = [
  METRICS_LIST.MAX_AADT,
  METRICS_LIST.LOCAL_POPULATION_DENSITY,
  METRICS_LIST.SUM_AADT,
  METRICS_LIST.NUMBER_HPC_STATIONS,
  METRICS_LIST.AVERAGE_CHARGES_STATION_PER_DAY,
  METRICS_LIST.HIGHEST_PERFORMING_COMPETITOR,
];

/** Advanced metrics, always requested together with the default set. */
export const ADVANCED_LOCATION_METRICS: readonly MetricsList[] = [
  METRICS_LIST.CAPTURED_AADT,
  METRICS_LIST.FORECAST,
];

/**
 * Per-location GeoJSON layers still fetched by location id. The whole road
 * network and every competitor station come from MVT tiles instead — see
 * `TRAFFIC_TILES_PATH` / `COMPETITORS_TILES_PATH` — so only the two layers with
 * no tile equivalent are left here.
 */
export const GEOSPATIAL_LAYER_LIST = {
  MAX_AADT: 'max_aadt_geojson',
  CAPTURED_AADT: 'captured_aadt_geojson',
} as const;

export type GeospatialLayerList =
  (typeof GEOSPATIAL_LAYER_LIST)[keyof typeof GEOSPATIAL_LAYER_LIST];

import type { StationRangeKey } from './stationIcons';

export interface StationColorConfig {
  color: string;
  id: string;
  rangeKey: StationRangeKey;
  label: string;
}

// Station marker colors for competitor stations based on charges per station per day
export const STATION_COLORS: Record<StationRangeKey, StationColorConfig> = {
  DEFAULT: {
    color: '#CCCCCC',
    id: 'competitor-default',
    rangeKey: 'DEFAULT',
    label: '',
  },
  RANGE_1_10: {
    color: '#9F9FED',
    id: 'competitor-1-10',
    rangeKey: 'RANGE_1_10',
    label: '< 1 to 10',
  },
  RANGE_10_15: {
    color: '#A36CD9',
    id: 'competitor-10-15',
    rangeKey: 'RANGE_10_15',
    label: '10-15',
  },
  RANGE_15_20: {
    color: '#BA2695',
    id: 'competitor-15-20',
    rangeKey: 'RANGE_15_20',
    label: '15-20',
  },
  RANGE_20_25: {
    color: '#BA2670',
    id: 'competitor-20-25',
    rangeKey: 'RANGE_20_25',
    label: '20-25',
  },
  RANGE_25_30: {
    color: '#D94680',
    id: 'competitor-25-30',
    rangeKey: 'RANGE_25_30',
    label: '25-30',
  },
  RANGE_30_50: {
    color: '#C4372F',
    id: 'competitor-30-50',
    rangeKey: 'RANGE_30_50',
    label: '30-50',
  },
  RANGE_50_PLUS: {
    color: '#EAB308',
    id: 'competitor-50-plus',
    rangeKey: 'RANGE_50_PLUS',
    label: '50+',
  },
};

/** The charge bands a user can filter: every range but the unlabeled default. */
export const STATION_CHARGE_CHIPS = Object.values(STATION_COLORS).filter((c) => c.label !== '');

export const STATION_CHARGE_FILTERS = [
  STATION_COLORS.RANGE_1_10.label,
  STATION_COLORS.RANGE_10_15.label,
  STATION_COLORS.RANGE_15_20.label,
  STATION_COLORS.RANGE_20_25.label,
  STATION_COLORS.RANGE_25_30.label,
  STATION_COLORS.RANGE_30_50.label,
  STATION_COLORS.RANGE_50_PLUS.label,
] as const;

export type StationChargeFilters = (typeof STATION_CHARGE_FILTERS)[number];

export enum SourceId {
  DASHED_CIRCLE = 'dashed-circle-source',
  /** Dotted line from a place to the cursor while a location is being dropped from it. */
  PLACE_CURSOR_LINK = 'place-cursor-link-source',
  /** Dotted line from a selected location to the place it was created from. */
  PLACE_LOCATION_LINK = 'place-location-link-source',
  // The location's highest-AADT segments. Stay GeoJSON: useMapInteractions
  // calls setFeatureState with a string id and no sourceLayer.
  MAX_AADT = 'max-aadt-source',
  MAX_AADT_CIRCLE = 'max-aadt-circle-source',
  HOVER_TRAFFIC_CIRCLE = 'hover-circle-traffic-source',

  /** Locations and mirrored deals both: Mapbox clusters per source, so one mark needs one source. */
  CLUSTERED_MARKS = 'clustered-marks-source',
  /**
   * The one location the URL selects, held apart and unclustered so it is never absorbed into a
   * cluster and always shows its selected mark. Disjoint from CLUSTERED_MARKS.
   */
  SELECTED_LOCATION = 'selected-location-source',
  ROUTING = 'routing-source',
  CAPTURED_AADT = 'captured-aadt-source',
  CAPTURED_AADT_MARKER = 'captured-aadt-marker-source',

  // Map-level vector tiles: the whole road network and every competitor station,
  // both independent of any selected location.
  TRAFFIC_TILES = 'traffic-tiles-source',
  COMPETITORS_TILES = 'competitors-tiles-source',
  /** The one winning station for the selected location. Not a tile feature. */
  HIGHEST_PERFORMING_COMPETITOR = 'highest-performing-competitor-source',
  /**
   * The deal whose card is open, drawn as its own pin whatever the zoom so it never folds into a
   * cluster. Disjoint from CLUSTERED_MARKS: a deal sits in exactly one of the two sources.
   */
  SELECTED_DEAL = 'selected-deal-source',

  // Hunting zones map overlay
  ZONES = 'zones-source',

  // Map single-zone highlight — the focused/selected zone
  SELECTED_ZONE = 'selected-zone-source',
}

export enum LayerId {
  DASHED_CIRCLE = 'dashed-circle-layer',
  PLACE_CURSOR_LINK = 'place-cursor-link-layer',
  PLACE_LOCATION_LINK = 'place-location-link-layer',
  TRAFFIC_MAX_AADT = 'traffic-max-aadt-layer',
  MAX_AADT_CIRCLE = 'max-aadt-circle-layer',
  HOVER_TRAFFIC_CIRCLE = 'hover-circle-traffic-layer',

  /** The one mark every cluster gets, showing a count per kind. */
  CLUSTER_CHIP = 'cluster-chip-layer',
  LOCATION_UNCLUSTERED = 'location-unclustered-layer',
  // The selected location over its own unclustered source, above the rest.
  SELECTED_LOCATION_MARK = 'selected-location-mark-layer',
  ROUTING = 'routing-layer',
  ROUTING_BORDER = 'routing-border-layer',
  CAPTURED_AADT_BORDER = 'captured-aadt-border-layer',
  CAPTURED_AADT = 'captured-aadt-layer',
  CAPTURED_AADT_MARKER = 'captured-aadt-marker-layer',

  // Map-level traffic vector tiles — bottom of the middle slot, below the
  // zones overlay and the per-location max-AADT layers
  TRAFFIC_TILES = 'traffic-tiles-layer',

  // Last quartile of sessions, from the opening zoom until the station tiers.
  COMPETITORS_HEATMAP = 'competitors-heatmap-layer',
  // Transparent circles on the same cells. A heatmap layer does not answer the
  // cursor, so these carry the brush and the hover card.
  COMPETITORS_HEATMAP_HIT = 'competitors-heatmap-hit-layer',

  // One layer per tier of the backend's level-of-detail ladder.
  COMPETITORS_TILES = 'competitors-tiles-layer',
  COMPETITORS_TILES_DENSE = 'competitors-tiles-dense-layer',
  // Crown on the winning station, drawn over that station's pin.
  HIGHEST_PERFORMING_COMPETITOR_CROWN = 'highest-performing-competitor-crown-layer',

  // Mirrored HubSpot deals: DEALS_POINT paints the resting dot, DEALS_RING the
  // hover/open ring over the same source.
  DEALS_POINT = 'deals-point-layer',
  DEALS_RING = 'deals-ring-layer',
  // The unclustered pair over SELECTED_DEAL, added after the clustered ones so an unfolded
  // pin wins an overlap with a neighbouring cluster. Both draw unconditionally: an unfolded
  // deal keeps its resting dot and carries the armed mark above it.
  SELECTED_DEAL_POINT = 'selected-deal-point-layer',
  SELECTED_DEAL_RING = 'selected-deal-ring-layer',

  // A charger at the mark. Shown with the 3D view; the symbol above it keeps the color.
  STATION_MODEL_COMPETITORS = 'station-model-competitors-layer',
  STATION_MODEL_ELECTRA = 'station-model-electra-layer',
  STATION_MODEL_DEALS = 'station-model-deals-layer',
  STATION_MODEL_SELECTED_DEALS = 'station-model-selected-deals-layer',
  STATION_MODEL_LOCATIONS = 'station-model-locations-layer',
  STATION_MODEL_SELECTED_LOCATION = 'station-model-selected-location-layer',

  // Hunting zones map overlay — rendered below location markers
  ZONES_FILL = 'zones-fill-layer',
  ZONES_OUTLINE = 'zones-outline-layer',
  ZONES_POINT = 'zones-point-layer',

  // The wide-zoom form of the same source: one counted bubble per cluster
  ZONE_CLUSTER = 'zone-cluster-layer',
  ZONE_CLUSTER_OUTER_RING = 'zone-cluster-outer-ring-layer',
  ZONE_CLUSTER_COUNT = 'zone-cluster-count-layer',

  // Map single-zone highlight — the focused/selected zone, drawn on top of the
  // zones overlay so neighbours read as de-emphasised.
  SELECTED_ZONE_FILL = 'selected-zone-fill-layer',
  SELECTED_ZONE_OUTLINE = 'selected-zone-outline-layer',
  // The same selection below the outline zoom, where the polygon is too small
  // to find. A hexagon on the zone centroid, the zones pictogram, hidden once
  // the polygon paints.
  SELECTED_ZONE_MARKER = 'selected-zone-marker-layer',
}

/** Every charging-station model. The 3D control shows and hides them together. */
export const STATION_MODEL_LAYER_IDS = [
  LayerId.STATION_MODEL_COMPETITORS,
  LayerId.STATION_MODEL_ELECTRA,
  LayerId.STATION_MODEL_DEALS,
  LayerId.STATION_MODEL_SELECTED_DEALS,
  LayerId.STATION_MODEL_LOCATIONS,
  LayerId.STATION_MODEL_SELECTED_LOCATION,
] as const;

/**
 * Minimum zoom at which zone outlines are displayed. Equal to
 * `TRAFFIC_TILES_MIN_ZOOM` on purpose: outlines enter with the motorway network,
 * so the two map-level layers appear together. Below it the same source carries
 * counted clusters instead — see `ZONE_CLUSTERS_MIN_ZOOM`.
 */
export const ZONES_MIN_ZOOM = 8;

/**
 * Minimum zoom at which the zone clusters are displayed, and the source's
 * `minzoom`. It is the tile endpoint's own floor (`tile.MinZoom`), so a request
 * below it is a 400, not a 204. The backend decides which form a tile carries, so
 * this pair of thresholds mirrors `zonePolygonsFromZoom` in `tile/zones.go`:
 * moving one without the other leaves a zoom painting nothing.
 */
export const ZONE_CLUSTERS_MIN_ZOOM = 1;

/**
 * Prefix of the per-owner count attributes a cluster carries: one per owner, keyed
 * `<prefix><email>`, holding that owner's zone count inside the cluster. Mirrors
 * `tile.ZoneClusterOwnerCountPrefix` — renaming it on one side of the wire leaves the
 * filtered cluster labels reading 0.
 */
export const ZONE_CLUSTER_OWNER_COUNT_PREFIX = 'owner_zone_count_';
/** Cluster type totals; owner/type intersections append `|<type>` to the owner key above. */
export const ZONE_CLUSTER_TYPE_COUNT_PREFIX = 'zone_type_count_';

/** Hunting-zones MVT endpoint, relative to the API base URL. */
export const ZONES_TILES_PATH = 'tiles/{z}/{x}/{y}?layer=zones';

/** Name of the layer inside the hunting-zones MVT tiles. */
export const ZONES_TILES_SOURCE_LAYER = 'zones';

/**
 * Highest zoom worth requesting: the deepest the pyramid is built to
 * (`maxBuildableZoom` in `materialize_map_tile_layers.go`). Mapbox over-zooms past it.
 */
export const ZONES_TILES_MAX_ZOOM = 12;

/**
 * Traffic MVT endpoint, relative to the API base URL. Mapbox GL substitutes the
 * `{z}/{x}/{y}` placeholders, so they must not be URL-encoded — see
 * `HttpService.buildTileUrl`.
 */
export const TRAFFIC_TILES_PATH = 'tiles/{z}/{x}/{y}?layer=traffic';

/** Name of the layer inside the traffic MVT tiles. */
export const TRAFFIC_TILES_SOURCE_LAYER = 'traffic';

/** Lowest zoom the traffic tile endpoint serves. */
export const TRAFFIC_TILES_MIN_ZOOM = 8;

/** Highest zoom the traffic tile endpoint serves (over-zoomed above it). */
export const TRAFFIC_TILES_MAX_ZOOM = 16;

/** Competitor-stations MVT endpoint, relative to the API base URL. */
export const COMPETITORS_TILES_PATH = 'tiles/{z}/{x}/{y}?layer=competitors';

/** Name of the layer inside the competitor-stations MVT tiles. */
export const COMPETITORS_TILES_SOURCE_LAYER = 'competitors';

/**
 * Lowest zoom of the session heatmap. Mirrors `tile.CompetitorHeatmapFromZoom`,
 * the widest zoom served, so zooming all the way out still shows the field.
 */
export const COMPETITORS_HEATMAP_MIN_ZOOM = 1;

/**
 * Cells along one axis of a heatmap tile. Mirrors `tile.CompetitorHeatmapGridCells`.
 * A vector tile is 512px, so at a whole zoom the cell centers sit 512/48 px apart.
 */
export const COMPETITOR_HEATMAP_GRID_CELLS = 48;

/**
 * Last quartile of sessions per station on the station colors. A low quartile
 * stays on the pale purple and a high quartile reaches yellow. `density` is the
 * heatmap weight and the color stop. The sidebar prints `label`.
 */
export const COMPETITOR_HEATMAP_RAMP = [
  { sessions: 5, density: 0.06, color: STATION_COLORS.RANGE_1_10.color, label: '5' },
  { sessions: 10, density: 0.18, color: STATION_COLORS.RANGE_10_15.color, label: '10' },
  { sessions: 15, density: 0.32, color: STATION_COLORS.RANGE_15_20.color, label: '15' },
  { sessions: 20, density: 0.46, color: STATION_COLORS.RANGE_20_25.color, label: '20' },
  { sessions: 25, density: 0.56, color: STATION_COLORS.RANGE_25_30.color, label: '25' },
  { sessions: 30, density: 0.76, color: STATION_COLORS.RANGE_30_50.color, label: '30' },
  { sessions: 50, density: 1, color: STATION_COLORS.RANGE_50_PLUS.color, label: '50' },
] as const;

/** Lowest zoom the competitor tiles carry individual stations. */
export const COMPETITORS_TILES_MIN_ZOOM = 10;

/** Highest zoom worth requesting: past it a tile re-cuts the same stations. */
export const COMPETITORS_TILES_MAX_ZOOM = 12;

/** Lowest zoom serving every station, not just Electra plus the charges floor. */
export const COMPETITORS_DENSE_MIN_ZOOM = 12;

/** Mirrors `tile.stationsChargesFloor`. */
export const COMPETITORS_DENSE_CHARGES_FLOOR = 20;

/** Other HPC stations within 2 km; mirrored by tile.StationSparseMaxNeighbors. */
export const COMPETITORS_SPARSE_MAX_NEIGHBORS = 6;

export const COMPETITORS_FADE_DURATION_MS = 500;

/**
 * Offsets a tier's fade above the zoom serving it: its features are absent from
 * the tiles below that zoom, so the tile swap would drop them mid-fade-out.
 */
export const COMPETITORS_FADE_LEAD_ZOOM = 0.5;

/** Our own stations in the competitors dataset, mirroring `tile.CPOElectra`. */
export const CPO_ELECTRA = 'electra';

/** Minimum HPC spots threshold for the low-HPC station filter. */
export const MIN_HPC_SPOTS = 4;

/** Carried on every feature of `SourceId.CLUSTERED_MARKS`. */
export enum MarkKind {
  LOCATION = 'location',
  DEAL = 'deal',
}

/** Declared by the source's `clusterProperties`, read by the chip. */
export const CLUSTER_COUNT_PROPERTY: Record<MarkKind, string> = {
  [MarkKind.LOCATION]: 'location_count',
  [MarkKind.DEAL]: 'deal_count',
};

/** The highest zoom that still clusters; supercluster reads it as inclusive. */
export const CLUSTER_MAX_ZOOM = 10;

/** In pixels, against the chip's own width so two chips cannot overlap without merging. */
export const CLUSTER_RADIUS = 50;

export const CLUSTER_MIN_POINTS = 2;

// Traffic colors based on AADT (Average Annual Daily Traffic) ranges
// These match the TRAFFIC_AADT_LINE_COLOR interpolation in MapLayersService
export const TRAFFIC_COLORS = {
  RANGE_0_1000: { id: '0-1k', color: '#35C497', label: '< 1k' },
  RANGE_1000_8000: { id: '1k-8k', color: '#5DC73A', label: '1k to 8k' },
  RANGE_8000_15000: { id: '8k-15k', color: '#FFC51A', label: '8k to 15k' },
  RANGE_15000_44000: { id: '15k-44k', color: '#FC8700', label: '15k to 44k' },
  RANGE_44000_PLUS: { id: '44k+', color: '#F14C00', label: '44k+' },
} as const;

export const TRAFFIC_FILTERS = [
  TRAFFIC_COLORS.RANGE_0_1000.id,
  TRAFFIC_COLORS.RANGE_1000_8000.id,
  TRAFFIC_COLORS.RANGE_8000_15000.id,
  TRAFFIC_COLORS.RANGE_15000_44000.id,
  TRAFFIC_COLORS.RANGE_44000_PLUS.id,
] as const;

export type TrafficFilters = (typeof TRAFFIC_FILTERS)[number];

// Captured AADT segment colors (up to 2 segments supported)
// Using vibrant, distinct colors not used elsewhere on the map
// Each segment has a main color and a darker border color for better visibility
export const CAPTURED_AADT_SEGMENT_COLORS = {
  SEGMENT_0: {
    id: '0',
    color: '#06B6D4',
    borderColor: '#0E7490',
    label: 'Segment 1',
  }, // Cyan / Dark Cyan
  SEGMENT_1: {
    id: '1',
    color: '#EC4899',
    borderColor: '#BE185D',
    label: 'Segment 2',
  }, // Pink / Dark Pink
} as const;

export const CAPTURED_AADT_SEGMENT_IDS = [
  CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
  CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
] as const;

export type CapturedAadtSegmentId = (typeof CAPTURED_AADT_SEGMENT_IDS)[number];

// Captured AADT segment marker icon names (keyed by segment id)
export const CAPTURED_AADT_MARKER_ICONS = {
  [CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id]: 'captured-aadt-marker-1',
  [CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id]: 'captured-aadt-marker-2',
} as const;

// Captured AADT grade values - must match CapturedAadtGrade type in @/types/api
export const CAPTURED_AADT_GRADES = [0, 1, 2, 3] as const;
export const CAPTURED_AADT_MAX_GRADE = Math.max(...CAPTURED_AADT_GRADES);

// Grade-based driving time display labels
export const CAPTURED_AADT_GRADE_LABELS: Record<(typeof CAPTURED_AADT_GRADES)[number], string> = {
  0: '>4min',
  1: '3-4min',
  2: '2-3min',
  3: '<2min',
};
